import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Send, Loader, CheckCircle, AlertCircle, Paperclip, X } from 'lucide-react';
import { QuoteField, QuoteRepeater } from './QuoteField';
import { labelStyle, helpStyle } from './quoteStyles';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const defaultsFor = (fields) => Object.fromEntries(fields.filter((f) => f.default !== undefined).map((f) => [f.key, String(f.default)]));

const buildInitial = (schema) => {
  const answers = {};
  schema.sections.forEach((s) => s.fields.forEach((f) => {
    if (f.type === 'repeater') answers[f.key] = [defaultsFor(f.fields)];
    else if (f.default !== undefined) answers[f.key] = String(f.default);
  }));
  return answers;
};

const findRepeaters = (schema) => schema.sections.flatMap((section) => section.fields.filter((field) => field.type === 'repeater'));

const visible = (field, answers) => !field.showIf || answers[field.showIf.key] === field.showIf.value;

const SectionCard = ({ title, step, children }) => (
  <section style={{ backgroundColor: 'var(--bg-page)', border: '1px solid var(--border-medium)', borderRadius: '12px', padding: '2rem' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
      <span style={{ width: '28px', height: '28px', borderRadius: '50%', backgroundColor: 'var(--brand-primary)', color: '#fff', fontSize: '0.85rem', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{step}</span>
      <h3 className="heading-3" style={{ margin: 0, fontSize: '1.25rem' }}>{title}</h3>
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '1.25rem' }} className="quote-grid">{children}</div>
  </section>
);

export const QuoteForm = ({ service, schema, contactFields, initialContact = {} }) => {
  const [answers, setAnswers] = useState(() => buildInitial(schema));
  const [contact, setContact] = useState(initialContact);
  const [files, setFiles] = useState([]);
  const [state, setState] = useState({ loading: false, error: '', quoteId: null });
  const [startedAt] = useState(() => Date.now());

  useEffect(() => {
    setAnswers(buildInitial(schema));
    setState({ loading: false, error: '', quoteId: null });
  }, [schema]);

  const repeaters = useMemo(() => findRepeaters(schema), [schema]);

  const setAnswer = (key, value) => {
    setAnswers((prev) => {
      const next = { ...prev, [key]: value };
      repeaters.filter((r) => r.countKey === key).forEach((r) => {
        const n = Math.min(Math.max(parseInt(value, 10) || 1, 1), 20);
        const items = [...(prev[r.key] || [])];
        while (items.length < n) items.push(defaultsFor(r.fields));
        next[r.key] = items.slice(0, n);
      });
      return next;
    });
  };

  const addFiles = (e) => {
    const picked = Array.from(e.target.files);
    setFiles((prev) => {
      const next = [...prev, ...picked].slice(0, 10);
      if (next.some((f) => f.size > 10 * 1024 * 1024) || next.reduce((t, f) => t + f.size, 0) > 25 * 1024 * 1024) {
        setState((s) => ({ ...s, error: 'Each file must be under 10 MB and all attachments under 25 MB total.' }));
        return prev;
      }
      return next;
    });
    e.target.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setState({ loading: true, error: '', quoteId: null });
    const body = new FormData();
    body.append('service', service);
    body.append('contact', JSON.stringify(contact));
    body.append('answers', JSON.stringify(answers));
    body.append('website', '');
    body.append('form_started_at', String(startedAt));
    files.forEach((f) => body.append('files', f));
    try {
      const res = await fetch(`${BACKEND_URL}/api/quote-requests`, { method: 'POST', body });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || 'Unable to submit your request.');
      setState({ loading: false, error: '', quoteId: data.quote_id });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setState({ loading: false, error: err.message, quoteId: null });
    }
  };

  if (state.quoteId) {
    return (
      <div data-testid="quote-success" style={{ textAlign: 'center', padding: '4rem 2rem', backgroundColor: 'var(--bg-page)', border: '1px solid var(--border-medium)', borderRadius: '12px' }}>
        <CheckCircle size={64} color="var(--brand-primary)" style={{ marginBottom: '1.5rem' }} />
        <h3 className="heading-2" style={{ marginBottom: '1rem' }}>Quote Request Received</h3>
        <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
          We have everything we need. Your {schema.label.toLowerCase()} quote will be emailed within 24 hours.
        </p>
        <p className="body-small" data-testid="quote-success-id" style={{ color: 'var(--text-primary)', fontWeight: '600' }}>Quote ID: {state.quoteId}</p>
        <p className="body-small" style={{ color: 'var(--text-secondary)', marginTop: '1.5rem' }}>
          Reply to the confirmation email if anything needs to be corrected, or <Link to={`/status?id=${state.quoteId}`} data-testid="quote-success-status-link" style={{ color: 'var(--brand-primary)', fontWeight: '600' }}>check your request status</Link> any time.
        </p>
      </div>
    );
  }

  let step = 0;
  return (
    <form onSubmit={handleSubmit} data-testid="quote-form" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ position: 'absolute', left: '-9999px', opacity: 0 }} defaultValue="" />
      {schema.sections.map((section) => {
        step += 1;
        return (
          <SectionCard key={section.title} title={section.title} step={step}>
            {section.fields.filter((f) => visible(f, answers)).map((field) => (
              field.type === 'repeater'
                ? <QuoteRepeater key={field.key} field={field} items={answers[field.key] || []} onChange={(items) => setAnswer(field.key, items)} />
                : <QuoteField key={field.key} field={field} value={answers[field.key]} onChange={(v) => setAnswer(field.key, v)} />
            ))}
          </SectionCard>
        );
      })}

      <SectionCard title="Your contact information" step={step + 1}>
        {contactFields.map((field) => (
          <QuoteField key={field.key} field={field} value={contact[field.key]} idPrefix="contact-" onChange={(v) => setContact((p) => ({ ...p, [field.key]: v }))} />
        ))}
        <div style={{ gridColumn: 'span 12' }}>
          <label style={labelStyle}>Attach documents (optional)</label>
          <label htmlFor="quote-files" data-testid="quote-file-dropzone" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1rem', border: '1px dashed var(--border-medium)', borderRadius: '8px', cursor: 'pointer', backgroundColor: 'var(--bg-card)', color: 'var(--text-secondary)' }}>
            <Paperclip size={18} color="var(--brand-primary)" /> Click to attach PDFs, Word files or photos (up to 10 files, 10 MB each)
          </label>
          <input id="quote-files" data-testid="quote-file-input" type="file" multiple accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.heic" onChange={addFiles} style={{ display: 'none' }} />
          {files.length > 0 && (
            <ul data-testid="quote-file-list" style={{ listStyle: 'none', padding: 0, margin: '0.75rem 0 0', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {files.map((f, i) => (
                <li key={`${f.name}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0.75rem', borderRadius: '999px', backgroundColor: 'rgba(44, 90, 160, 0.08)', fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                  {f.name}
                  <button type="button" aria-label={`Remove ${f.name}`} data-testid={`quote-file-remove-${i}`} onClick={() => setFiles((p) => p.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', padding: 0 }}>
                    <X size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <p style={helpStyle}>Uploading the documents lets us confirm page counts and requirements for an accurate quote.</p>
        </div>
      </SectionCard>

      {state.error && (
        <div data-testid="quote-error" role="alert" style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', padding: '1rem 1.25rem', borderRadius: '8px', backgroundColor: '#fff5f5', border: '1px solid #feb2b2', color: '#c53030' }}>
          <AlertCircle size={20} /> <span>{state.error}</span>
        </div>
      )}

      <button type="submit" className="btn-primary" data-testid="quote-submit-button" disabled={state.loading}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', fontSize: '1.1rem', padding: '1.1em 2em', opacity: state.loading ? 0.7 : 1, cursor: state.loading ? 'not-allowed' : 'pointer' }}>
        {state.loading ? <><Loader size={18} className="animate-spin" /> Sending...</> : <><Send size={18} /> Send My {schema.label} Quote Request</>}
      </button>
      <p className="body-small" style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
        No obligation. We reply within 24 hours - usually much sooner.
      </p>
    </form>
  );
};
